import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  BILLING_CYCLE,
  SelectedRowAccountInfo,
  UsageLayoutOutletContext,
} from "api/interfaces/subscription.interface";
import Tabs from "components/atoms/tabs/Tabs";
import Status, { StatusType } from "components/blocks/status/Status";
import { TableTemplate } from "components/templates/default/table/TableTemplate";
import { useMenu } from "hook/useMenu";
import { useCallback, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { Option } from "utils/options";
import PlanSummary from "./planSummary/index";
import { UsagePlan } from "../SubscriptionStyled";
import useTableQueryParams from "hook/useTableQueryParams";
import UpgradeCycleModal from "../modal/upgradeCycle";
import AddCloudModal, {
  CloudModalAccount,
} from "pages/subscription/modal/addCloud/AddCloudModal";
import { PLAN_TYPE } from "api/interfaces/planInterface.interface";
import usePlanStatCard from "hook/usePlanStatCard";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { isYearlyBillingCycle } from "utils/priceUtils";

const UsageTabs = [
  {
    label: "Organization",
    value: "/subscription/usage/organization",
  },
  {
    label: "Event History",
    value: "/subscription/usage/history",
  },
  {
    label: "Bill to Date",
    value: "/subscription/usage/billing",
  },
];

export default function UsageLayout(): JSX.Element {
  const location = useLocation();
  const { menuNavigation, buildEntryNavigateProps } = useMenu({});
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { planStorageUpdated } = useAppSelector(
    (state) => state.subscriptionStomp
  );
  const isYearly = isYearlyBillingCycle(selectedAccount.cycle);

  const {
    searchParams,
    updateQueryInfo,
    onClickSearch,
    onHeaderCell,
    onChangePage,
  } = useTableQueryParams();

  const [summaryType, setSummaryType] = useState<PLAN_TYPE>(PLAN_TYPE.CHANNEL);
  const [selectedGraphDate, setSelectedGraphDate] = useState<string>("");
  const [buttons, setButtons] = useState<React.ReactNode>(null);
  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
  });
  const [addCloudModalAccount, setAddCloudModalAccount] =
    useState<CloudModalAccount | null>(null);

  const [isUpgradeModal, setIsUpgardeModal] = useState<boolean>(false);

  const selectedRowAccount: SelectedRowAccountInfo = useMemo(() => {
    const rowAccountId = searchParams.get("rowAccountId");
    const rowAccountLevel = searchParams.get("rowAccountLevel");
    const rowAccountNumber = searchParams.get("rowAccountNumber");
    const rowParentLevel = searchParams.get("rowParentLevel");
    const rowIsUsagePaid = searchParams.get("rowIsUsagePaid");

    if (rowAccountId && rowAccountNumber && rowAccountLevel && rowParentLevel) {
      return {
        accountId: rowAccountId,
        accountLevel: rowAccountLevel as LEVEL_TYPE,
        accountNumber: rowAccountNumber,
        parentLevel: rowParentLevel as LEVEL_TYPE,
        isUsagePaid: rowIsUsagePaid === "true" ? true : false,
      };
    }

    return {
      accountId: selectedAccount.accountId,
      accountLevel: selectedAccount.accountLevel,
      accountNumber: selectedAccount.accountNumber,
      parentLevel: selectedAccount.parentLevel as LEVEL_TYPE,
      isUsagePaid: selectedAccount.isUsagePaid,
    };
  }, [searchParams, selectedAccount]);

  const { summary: statCardSummary } = usePlanStatCard({
    accountId: selectedRowAccount.accountId,
    cycle: selectedAccount.cycle ?? BILLING_CYCLE.MONTHLY,
    type: summaryType,
  });

  const outletContext: UsageLayoutOutletContext = {
    updateQueryInfo,
    onHeaderCell,
    onChangePage,
    setStatus,
    setButtons,
    selectedRowAccount,
    summary: statCardSummary,
    selectedGraphDate,
    onOpenAddCloudModal: (account: CloudModalAccount) =>
      setAddCloudModalAccount(account),
  };

  const isBilling = location.pathname.includes("/usage/billing");

  const onUpgradeCycle = useCallback(() => {
    setIsUpgardeModal((prev) => !prev);
  }, []);

  const subscriptionRemountKey = useMemo(() => {
    return planStorageUpdated.updatedDate
      ? `storage-${planStorageUpdated.updatedDate.toISOString()}`
      : "default";
  }, [planStorageUpdated]);

  if (!selectedAccount.isUsagePaid) {
    return (
      <Navigate
        {...buildEntryNavigateProps("/subscription/usage/signup")}
        replace
      />
    );
  }

  return (
    <>
      <UsagePlan.OutletWrapper
        $level={selectedRowAccount.accountLevel}
        key={subscriptionRemountKey}
      >
        {isUpgradeModal && <UpgradeCycleModal onModal={onUpgradeCycle} />}
        {addCloudModalAccount && (
          <AddCloudModal
            onModal={() => setAddCloudModalAccount(null)}
            account={addCloudModalAccount}
            cycle={selectedAccount.cycle ?? BILLING_CYCLE.MONTHLY}
          />
        )}
        <PlanSummary
          summary={statCardSummary}
          summaryType={summaryType}
          onUpgradeCycle={onUpgradeCycle}
          setSummaryType={setSummaryType}
          onOpenAddCloudModal={(account: CloudModalAccount) => {
            setAddCloudModalAccount(account);
          }}
          onGraphDateChange={setSelectedGraphDate}
        />
        {(!isYearly || summaryType !== PLAN_TYPE.STORAGE) && (
          <TableTemplate
            titleComponent={
              <Tabs
                tabs={UsageTabs as unknown as Option[]}
                currentTab={location.pathname}
                onChangeTab={(value) =>
                  menuNavigation({
                    to: value,
                  })
                }
              />
            }
            centerChildren={isBilling ? null : <Status status={status} />}
            noSearch={isBilling}
            keyword={searchParams.get("keyword") ?? ""}
            onClickSearch={onClickSearch}
            buttons={buttons}
            forwardSearch={null}
          >
            <Outlet context={outletContext} />
          </TableTemplate>
        )}
      </UsagePlan.OutletWrapper>
    </>
  );
}
